import { hasError } from '@repo/shared/api/helpers'
import { DocumentType } from '@repo/shared/dtos/document'
import { Button } from '@repo/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Textarea } from '@repo/ui/textarea'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { CloudUpload, PlusCircle } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { IoSparklesSharp } from 'react-icons/io5'

import { Uploader } from '@/components/beneficiary/FileUploader'
import { api } from '@/main.tsx'
import { storeActions } from '@/store/actions'
import { Note } from '@/store/document'
import { useStoreDispatch } from '@/store/hooks'
import { noteSchema } from '@/validation-schemas'

export function NoteCreator() {
  const { t, tc } = useT({ keyPrefix: 'noteCreator' })
  const [open, setOpen] = useState(false)
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <TooltipWrapper content={tc('createANote', { cfl: true })}>
          <Button onClick={() => setOpen(true)} variant="outline">
            <PlusCircle className="size-4" />
            {tc('note', { cfl: true })}
          </Button>
        </TooltipWrapper>
      </DialogTrigger>
      <DialogContent size="wide" disableOutsideClick>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <NoteForm onSubmitted={() => setOpen(false)} type={DocumentType.NOTE} />
      </DialogContent>
    </Dialog>
  )
}

export function DocumentUploader() {
  const dispatch = useStoreDispatch()
  const { t, tc } = useT({ keyPrefix: 'documentUploader' })
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" onClick={() => setOpen(true)}>
          <CloudUpload className="size-4" />
          {tc('uploadFile', { cfl: true })}
        </Button>
      </DialogTrigger>
      <DialogContent disableOutsideClick>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <div className="w-full">
          <Uploader
            processFile={async ({ file }) =>
              dispatch(storeActions.beneficiary.document.create({ file }))
            }
            onUploaded={() => setOpen(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function DocumentScanner() {
  const dispatch = useStoreDispatch()
  const { t, tc } = useT({ keyPrefix: 'documentScanner' })
  const [open, setOpen] = useState(false)

  const [content, setContent] = useState<string>()
  const [instructions, setInstructions] = useState<string>()
  const [file, setFile] = useState<File>()
  const [isUploading, setIsUploading] = useState(false)

  // Reset state when dialog is closed
  useEffect(() => {
    if (open) return
    setContent(undefined)
    setInstructions(undefined)
    setFile(undefined)
    setIsUploading(false)
  }, [open])

  const handleProcessFile = async ({
    organizationId,
    file,
  }: {
    organizationId: string
    file: File
  }) => {
    setIsUploading(true)
    return api.beneficiaries.one.processNote({
      organizationId,
      file,
      instructions,
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <TooltipWrapper content={tc('smartScan', { cfl: true })}>
          <Button variant="highlight" size="icon" onClick={() => setOpen(true)}>
            <IoSparklesSharp className="size-4" />
          </Button>
        </TooltipWrapper>
      </DialogTrigger>
      <DialogContent size={content ? 'wide' : 'default'} disableOutsideClick>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <Textarea
          className="h-40 resize-none"
          disabled={isUploading}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder={t('instructionsPlaceholder')}
        />
        <div className="flex items-center gap-2">
          <Uploader
            isSmart
            processFile={handleProcessFile}
            onUploaded={(response) => {
              setIsUploading(false)
              if (hasError(response)) return
              setContent(response.summary)
            }}
            onStoreFile={(file) => setFile(file)}
          />
        </div>
        {content && (
          <>
            <NoteForm
              type={DocumentType.NOTE_FROM_FILE_ANALYSIS}
              disabled={isUploading}
              newValues={{ content }}
              onSubmitted={() => {
                if (file) {
                  void dispatch(storeActions.beneficiary.document.create({ file }))
                }
                setOpen(false)
              }}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function NoteForm({
  editableNote,
  newValues,
  onSubmitted,
  disabled = false,
  type,
}: {
  disabled?: boolean
  editableNote?: Note
  newValues?: Pick<Note, 'content'>
  onSubmitted: () => void
  type:
    | DocumentType.NOTE
    | DocumentType.NOTE_FROM_FILE_ANALYSIS
    | DocumentType.NOTE_FROM_EMAIL_ANALYSIS
}) {
  const dispatch = useStoreDispatch()
  const { tc } = useT({ keyPrefix: 'noteEditor' })

  const defaultValues = useMemo(() => ({ content: editableNote?.content || '' }), [editableNote])

  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues,
    onSubmit: ({ content }) => {
      if (editableNote) {
        void dispatch(
          storeActions.beneficiary.document.update({
            id: editableNote.id,
            content,
          }),
        )
      } else {
        void dispatch(storeActions.beneficiary.document.create({ content, type }))
      }
      form.reset(defaultValues)
      onSubmitted()
    },
    schema: noteSchema.shape,
  })

  useEffect(() => {
    if (newValues) form.reset(newValues)
  }, [newValues, form])

  return (
    <Form {...form}>
      <form onSubmit={submit} className="space-y-2" onKeyDown={submitOnPressMetaEnter}>
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc('note', { cfl: true })}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={tc('note', { cfl: true })}
                  {...field}
                  disabled={disabled}
                  className="h-60 text-base lg:h-80 xl:h-96"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="submit" disabled={!isFormValid || disabled}>
            {tc(editableNote ? 'update' : 'create', { cfl: true })}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}
